import { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";

const LAST_UPDATED = "3 August 2026";

export default function Terms() {
  useEffect(() => { document.title = "Terms of Use | ImpactVest"; window.scrollTo(0, 0); }, []);
  return (
    <div>
      <Header />
      <main className="pt-28 md:pt-36 pb-24" data-testid="page-terms">
        <div className="iv-container max-w-3xl">
          <div className="iv-eyebrow mb-6">Terms of Use</div>
          <h1 className="font-serif-display text-[42px] md:text-[56px] leading-[1.02] tracking-tight text-iv-fg">
            Terms of Use.
          </h1>
          <p className="mt-4 font-mono text-[11px] tracking-[0.14em] uppercase text-iv-fg-3">
            Last updated: {LAST_UPDATED} · Applies to impactvest.com and the ImpactVest Test Workspace
          </p>

          <Section title="1. Acceptance">
            <p>By using impactvest.com or the ImpactVest Test Workspace you agree to these Terms of Use.
            If you do not agree, do not use the service. ImpactVest is a product of Gaiosis Systems
            ("we", "us", "our").</p>
          </Section>

          <Section title="2. What ImpactVest is — and is not">
            <ul>
              <li>ImpactVest is an <b>evidence-linked decision-support tool</b> that helps investors
              evaluate the evidence behind an opportunity and helps companies substantiate ESG and
              impact claims. It preserves reasoning, citations and human approvals.</li>
              <li>ImpactVest <b>does not</b> emit buy / hold / pass recommendations. It does not
              produce composite scores. It does not certify or assure claims. Framework mapping is
              presented as evidence organisation only.</li>
              <li>ImpactVest is not investment advice, legal advice, accounting advice or a substitute
              for independent professional judgment. Every decision remains yours.</li>
            </ul>
          </Section>

          <Section title="3. The Test Workspace">
            <p>The Test Workspace uses fictional data and is provided for demonstration and evaluation.
            Nothing generated in the Test Workspace is, or should be presented as, a real investment
            decision or a real company disclosure. Every fictional page carries a "Fictional data"
            watermark.</p>
          </Section>

          <Section title="4. Your responsibilities">
            <ul>
              <li>Register with truthful professional information. Registration for a decision-making
              role you do not hold is a breach of these Terms.</li>
              <li>Upload only material you are entitled to upload. You warrant that your uploads do not
              infringe third-party rights and comply with applicable law.</li>
              <li>Do not misuse the platform (reverse-engineer, scrape, attempt to bypass rate limits,
              upload malware, or disrupt other users).</li>
              <li>Keep your session credentials secure.</li>
            </ul>
          </Section>

          <Section title="5. Intellectual property">
            <p>ImpactVest, the sprout mark and the ImpactVest wordmark are trademarks of Gaiosis
            Systems. Your uploads remain yours; you grant us a limited licence solely to process them
            for the analysis you asked for.</p>
          </Section>

          <Section title="6. Availability and beta status">
            <p>The service is currently offered in public beta. Availability, features and pricing may
            change. Some capabilities are marked "planned" in our documentation and are not yet
            available; do not rely on them until they ship. Live payments are currently disabled.</p>
          </Section>

          <Section title="7. Warranties and limits of liability">
            <p>Except as required by law, the service is provided "as is". To the maximum extent
            permitted by law, Gaiosis Systems is not liable for indirect, incidental, consequential or
            punitive damages, or for lost profits or lost data. Nothing in these Terms limits any
            liability that cannot be limited by law (including fraud or death or personal injury
            caused by negligence).</p>
          </Section>

          <Section title="8. Termination">
            <p>You may stop using the service at any time. We may suspend or terminate access if you
            breach these Terms, if we discontinue the service, or if required by law. On termination,
            provisions that by nature should survive will survive (IP, liability, governing law).</p>
          </Section>

          <Section title="9. Governing law">
            <p>These Terms are governed by the laws of the jurisdiction in which Gaiosis Systems is
            established. Disputes will be resolved in the competent courts of that jurisdiction.</p>
          </Section>

          <Section title="10. Contact">
            <p>Questions about these Terms: <a className="iv-link-underline" href="mailto:office@impactvest.com">office@impactvest.com</a>.</p>
          </Section>

          <div className="mt-16 pt-8 border-t border-iv-line flex items-center justify-between gap-4 flex-wrap">
            <Link to="/legal/privacy" className="iv-link-underline text-[13.5px] text-iv-fg-2">Read the Privacy Notice →</Link>
            <Link to="/contact" className="iv-link-underline text-[13.5px] text-iv-fg-2">Contact us →</Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-12 pt-8 border-t border-iv-line">
      <h2 className="font-serif-display text-[24px] md:text-[28px] leading-tight text-iv-fg mb-4">{title}</h2>
      <div className="space-y-3 text-[15px] leading-relaxed text-iv-fg-2 [&_ul]:list-disc [&_ul]:ml-6 [&_li]:mt-2">{children}</div>
    </section>
  );
}
